// src/pages/Search.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useYouTube } from './useYoutube';
import { PlayerContext } from '../context/PlayerContext';
import './Search.css';

const Search = () => {
  const [searchQuery, setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [trendingMusic, setTrendingMusic] = useState([]);
  const [isSearching, setIsSearching]   = useState(false);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [loading, setLoading]           = useState(true);
  
  // Pagination state
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  // ✅ Context se sab kuch lo
  const { 
    playSong, 
    selectedSong, 
    playlist,
    setShowFullPlayer,
    setShowMiniPlayer 
  } = useContext(PlayerContext);
  
  const { initialized, searchSongs, getTrendingMusic, searchSongsWithToken } = useYouTube();

  // Initial trending load
  useEffect(() => {
    if (!initialized) return;
    (async () => {
      setLoading(true);
      try {
        const t = await getTrendingMusic(20);
        setTrendingMusic(t || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [initialized, getTrendingMusic]);

  // Search handler - first page
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setSearchResults([]);
    setNextPageToken(null);
    setHasMore(false);
    setTotalResults(0);
    
    try {
      const result = await searchSongs(searchQuery, 20);
      
      if (result) {
        // Handle both formats: array or object with pagination
        if (Array.isArray(result)) {
          setSearchResults(result);
          setHasMore(false);
        } else {
          setSearchResults(result.items || []);
          setNextPageToken(result.nextPageToken || null);
          setHasMore(!!result.nextPageToken);
          setTotalResults(result.totalResults || result.items?.length || 0);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearching(false);
    }
  };

  // Load more results
  const handleLoadMore = async () => {
    if (!searchQuery.trim() || !nextPageToken || loadingMore) return;
    
    setLoadingMore(true);
    try {
      const result = await searchSongs(searchQuery, 20, nextPageToken);
      
      if (result && result.items) {
        setSearchResults(prev => [...prev, ...result.items]);
        setNextPageToken(result.nextPageToken || null);
        setHasMore(!!result.nextPageToken);
      }
    } catch (e) {
      console.error('Load more error:', e);
    } finally {
      setLoadingMore(false);
    }
  };

  // Song select handler
  const handleSongSelect = (song) => {
    const initialPlaylist = searchResults.length > 0 ? searchResults : trendingMusic;
    playSong(song, initialPlaylist);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setNextPageToken(null);
    setHasMore(false);
    setTotalResults(0);
  };

  const formatViews = (v) => {
    if (!v) return '';
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return v;
  };

  if (!initialized || loading) {
    return (
      <div className="search-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading music...</p>
        </div>
      </div>
    );
  }

  const songs   = searchResults.length > 0 ? searchResults : trendingMusic;
  const heading = searchResults.length > 0
    ? `Results for "${searchQuery}"${totalResults > 0 ? ` (${totalResults}+ found)` : ''}`
    : '🔥 Trending Music';

  return (
    <div className="search-page">
      <div className="search-header">
        <h1>🎵 Music Search</h1>
        <p className="search-subtitle">Search millions of songs, artists, and albums</p>
        <div className="search-container">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              className="search-input"
              placeholder="Search for songs, artists, albums..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-btn" disabled={isSearching}>
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            {searchResults.length > 0 && (
              <button type="button" className="clear-btn" onClick={clearSearch}>
                Clear
              </button>
            )}
          </form>
        </div>
      </div>

      <div className="content-container">
        <h2>{heading}</h2>
        
        {isSearching && searchResults.length === 0 ? (
          <div className="loading-container">
            <div className="spinner" />
            <p>Searching...</p>
          </div>
        ) : songs.length > 0 ? (
          <>
            <div className="songs-grid">
              {songs.map((song) => (
                <SongCard
                  key={song.id}
                  song={song}
                  isActive={selectedSong?.id === song.id}
                  onSelect={handleSongSelect}
                  formatViews={formatViews}
                />
              ))}
            </div>
            
            {/* ✅ Load More Button */}
            {hasMore && searchResults.length > 0 && (
              <div className="load-more-container">
                <button 
                  className="load-more-btn" 
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="spinner-small" />
                      Loading...
                    </>
                  ) : (
                    `Load More Songs (${searchResults.length} loaded)`
                  )}
                </button>
              </div>
            )}
            
            {/* Results count */}
            {searchResults.length > 0 && !hasMore && (
              <p className="results-end">Showing all {searchResults.length} results</p>
            )}
          </>
        ) : (
          <p className="no-results">No results found</p>
        )}
      </div>
    </div>
  );
};

const SongCard = ({ song, isActive, onSelect, formatViews }) => (
  <div
    className={`song-card${isActive ? ' song-card--active' : ''}`}
    onClick={() => onSelect(song)}
  >
    <div style={{ position: 'relative' }}>
      <img src={song.thumbnail} alt={song.title} loading="lazy" />
      <div className="play-icon">▶</div>
    </div>
    <div className="song-info">
      <h3>{song.title}</h3>
      <p>{song.channelTitle}</p>
      {song.viewCount && (
        <p className="views">👁 {formatViews(song.viewCount)} views</p>
      )}
    </div>
  </div>
);

export default Search;